import { Auth0Provider, useAuth0 } from "@auth0/auth0-react"
import "@fontsource/inter/300.css"
import "@fontsource/inter/400.css"
import "@fontsource/inter/500.css"
import "@fontsource/inter/600.css"
import { CssBaseline, ThemeProvider } from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import "dayjs/locale/fr"
import * as Sentry from "@sentry/react"
import { SnackbarProvider } from "notistack"
import React, { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router"
import { useTranslation } from "react-i18next"
import { AppRoutes } from "./AppRoutes"
import { store } from "./app/apis"
import { ToastListener } from "./features/shared-module/components"
import { useWebSocket } from "./hooks/useWebSocket"
import "./i18n/i18n"
import "./styles.css"
import { lightTheme } from "./theme"
import { setAuth0 } from "./utils/auth0Utils"
import { DevAuth0Provider } from "./utils/DevAuth0Provider"

const isAgenticMode = !!import.meta.env.VITE_AGENTIC
const AppAuth0Provider = isAgenticMode ? DevAuth0Provider : Auth0Provider

const AppWrapper = () => {
  const auth0 = useAuth0()
  setAuth0(auth0)
  useWebSocket()
  const { i18n } = useTranslation()
  const locale = i18n.language === "fr" ? "fr" : "en"
  return (
    <ThemeProvider theme={lightTheme}>
      <SnackbarProvider
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        autoHideDuration={4000}
      >
        <CssBaseline />
        <ToastListener />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
          <AppRoutes />
        </LocalizationProvider>
      </SnackbarProvider>
    </ThemeProvider>
  )
}

if (import.meta.env.VITE_DISABLE_SENTRY !== "true") {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || "missing_dsn",
    sendDefaultPii: true,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT ||
      import.meta.env.MODE ||
      "development",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate:
      parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) || 1.0,
    tracePropagationTargets: import.meta.env
      .VITE_SENTRY_TRACE_PROPAGATION_TARGETS
      ? import.meta.env.VITE_SENTRY_TRACE_PROPAGATION_TARGETS.split(",")
      : ["localhost"],
    replaysSessionSampleRate:
      parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE) ||
      0.1,
    replaysOnErrorSampleRate:
      parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE) ||
      1.0,
  })
}

const App = () => (
  <StrictMode>
    <AppAuth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        // scope: "openid profile email",
        audience: "mbls-global-api",
        redirect_uri: window.location.origin,
      }}
      cacheLocation="localstorage"
    >
      <Provider store={store}>
        <AppWrapper />
      </Provider>
    </AppAuth0Provider>
  </StrictMode>
)

const root = createRoot(document.getElementById("root")!, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack)
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
})
root.render(<App />)
